import React from 'react';
import { Link } from 'react-router-dom';
import Seo from 'components/Seo';
import { Glow, InkFooter, InkNav, LOGO_64, LOGO_256, RELEASE, useInkRoot, useLightbox } from './shared';

/* Neevii Ink landing (/ink), ported from the approved prototype
   code/public/_design/prototype-v2.html (Oct 2026). Licence pricing is
   deliberately not shown yet (Lee, Oct 2026): "to be decided" only.
   Previous page kept at Docs/Neevii Ink/Landing Page/legacy-ink-index-v1.jsx. */

const HERO_SHOT = '/assets/images/ink/ui-hero-v06.webp';
const BG_VIDEO = '/assets/videos/Neevii_ink_background_streaks-1920.mp4';
const BG_POSTER = '/assets/videos/Neevii_ink_background_streaks-poster.webp';
const COLOUR_VIDEO = '/assets/videos/ink-colour-demo.mp4';
const COLOUR_POSTER = '/assets/videos/ink-colour-demo-poster.webp';
const GEN_VIDEO = '/assets/videos/ink-genai-demo.mp4';
const GEN_POSTER = '/assets/videos/ink-genai-demo-poster.webp';
const KEY_SPLIT_VIDEO = '/assets/videos/ink-key-split-demo.mp4';
const KEY_SPLIT_POSTER = '/assets/videos/ink-key-split-demo-poster.webp';

const CALLOUTS = [
  { side: 'l', top: '12%', c: '#CDB9FF', curve: 'M0 32 C45 32, 55 8, 100 8',
    text: <>Reference-grade <b>scene-linear</b> compositing engine</> },
  { side: 'r', top: '30%', c: '#F5B8FC', curve: 'M0 32 C45 32, 55 56, 100 56',
    text: <>Work <b>offline</b> in <b>air-gapped</b> studios with locally served models</> },
  { side: 'l', top: '48%', c: '#FFC29E', curve: 'M0 32 C45 32, 55 8, 100 8',
    text: <>Native on <b>Linux</b> &amp; Windows</> },
  { side: 'r', top: '66%', c: '#CDB9FF', curve: 'M0 32 C45 32, 55 56, 100 56',
    text: <>Tools familiar to <b>Nuke</b> &amp; <b>Photoshop</b> users</> },
  { side: 'l', top: '84%', c: '#F5B8FC', curve: 'M0 32 C45 32, 55 8, 100 8',
    text: <>Add an <b>OCIO</b> show LUT and share the same colour pipeline as <b>Nuke</b></> },
];

const ROUTES = [
  { k: 'Local', t: 'This machine', d: 'Models on your own GPU. Works with the network cable out.', tag: 'Air-gapped' },
  { k: 'Studio', t: 'Your servers', d: "Point at your studio's inference box. Data stays inside your network.", tag: 'On-prem' },
  { k: 'Cloud', t: 'Your API key', d: 'Optional. Use a provider you trust, with your own account and terms.', tag: 'Opt-in' },
];

const KEY_SPLIT_TOOLS = [
  {
    n: 'Tool 01 · Keying',
    k: 'Green / Blue Screen',
    d: 'A hybrid keyer for scene-linear float: AI finds the screen, then a colour key refines the edges, despills the green fringe and colour-corrects the plate — clean enough to drop straight behind your comp.',
    tag: 'AI + colour key',
  },
  {
    n: 'Tool 02 · Splitting',
    k: 'Scene Splitter',
    d: 'Builds depth-based mattes from a single flat image and splits it into grouped sky, background, midground and foreground layers — each one stays editable, so you can refine the split at any time.',
    tag: 'Depth layers',
  },
];

const inkSchema = {
  '@context': 'https://schema.org',
  '@type': 'SoftwareApplication',
  name: 'Neevii Ink',
  applicationCategory: 'DesignApplication',
  operatingSystem: 'Windows 10/11, Linux (Ubuntu 24.04+, x86_64)',
  softwareVersion: RELEASE.version.replace(/^v/, ''),
  description:
    'A reference-grade, scene-linear image editor for VFX on Linux and Windows: ACEScg and OCIO colour management, and local generative AI that runs fully offline. Free for personal use.',
  offers: { '@type': 'Offer', price: '0', priceCurrency: 'GBP', description: 'Free for personal use' },
  author: { '@type': 'Organization', name: 'Neevii' },
  downloadUrl: 'https://www.neevii.com/get-ink',
  featureList: [
    'Scene-linear ACEScg compositing engine, 16/32-bit float',
    'OCIO colour management with ACEScc / ACEScct viewing and show LUTs',
    'Local generative AI: Remove, Replace, Extend',
    'Runs fully offline for air-gapped studios',
    'Native on Linux and Windows',
  ],
};

function Callout({ side, top, c, curve, text }) {
  return (
    <div className={`call ${side}`} style={{ top, '--c': c }}>
      <span>{text}</span>
      <svg viewBox="0 0 100 64" preserveAspectRatio="none" aria-hidden="true">
        <path d={curve} />
      </svg>
    </div>
  );
}

function Zoomable({ className, label, item, open, children }) {
  return (
    <div
      className={className}
      role="button"
      tabIndex={0}
      aria-label={label}
      onClick={() => open(item)}
      onKeyDown={(e) => e.key === 'Enter' && open(item)}
    >
      {children}
    </div>
  );
}

export default function InkLandingPage() {
  useInkRoot();
  const [openLightbox, lightbox] = useLightbox();

  return (
    <div className="ink2">
      <Seo
        title="Neevii Ink: a scene-linear image editor built for VFX"
        description="A reference-grade, scene-linear image editor for Linux and Windows. The same OCIO colour pipeline as Nuke, and local AI that runs fully offline for air-gapped studios. Free for personal use."
        og={{ ogType: 'website' }}
        schema={inkSchema}
      />
      <Glow />
      <InkNav onHome />

      <section className="hero">
        <video autoPlay muted loop playsInline poster={BG_POSTER} aria-hidden="true">
          <source src={BG_VIDEO} type="video/mp4" />
        </video>
        <div className="wrap">
          <span className="badge mono">
            <img src={LOGO_64} alt="" width="26" height="26" />
            Free for personal use · {RELEASE.version} for Windows &amp; Linux
          </span>
          <h1>
            Paint with <span className="inkword">light.</span> Build for&nbsp;VFX.
          </h1>
          <p className="lead">
            A reference-grade, scene-linear image editor for Linux and Windows. Familiar to Nuke and Photoshop
            artists. Run it fully air-gapped with local AI, or connect your own cloud provider.
          </p>
          <div className="cta">
            <Link className="btn btn-p big" to="/get-ink#download">↓ Download</Link>
            <a className="btn btn-g big" href="#generative">▶ See it in action</a>
          </div>
          <div className="fine">
            <i />
            Free for personal use · built by a working VFX artist
          </div>
        </div>
        <div className="stage">
          <Zoomable
            className="frame"
            label="Enlarge the Neevii Ink screenshot"
            item={{ type: 'img', src: HERO_SHOT, alt: 'Neevii Ink interface' }}
            open={openLightbox}
          >
            <img
              src={HERO_SHOT}
              alt="Neevii Ink interface: an ACEScg grading session with layers, adjustments and OCIO view controls"
              width="1987"
              height="1163"
            />
          </Zoomable>
          {CALLOUTS.map((cl) => (
            <Callout key={cl.top} {...cl} />
          ))}
        </div>
      </section>

      <div className="strip">
        <span>Linux + Windows</span>
        <span>Offline &amp; air-gapped</span>
        <span>Scene-linear ACEScg</span>
        <span>Nuke &amp; Photoshop tools</span>
      </div>

      <section className="sec os">
        <div className="wrap center">
          <div className="mono eyebrow or">Linux &amp; Windows</div>
          <h2>
            One app. <span className="inkword">Both workstations.</span>
          </h2>
          <p className="lead">
            Photoshop never came to Linux. Neevii Ink runs natively on Linux and Windows, so an artist doesn&apos;t
            need a workstation of each.
          </p>
          <div className="plat">
            <div className="p lin">
              <small>Biggest difference</small>
              <b>Linux</b>
              <span>Ubuntu 24.04+ · x86_64</span>
              <Link className="btn btn-p" to="/get-ink#download">↓ Download for Linux</Link>
            </div>
            <div className="p">
              <small>Also native</small>
              <b>Windows</b>
              <span>Windows 10/11 · 64-bit</span>
              <Link className="btn btn-g" to="/get-ink#download">↓ Download for Windows</Link>
            </div>
          </div>
        </div>
      </section>

      <section className="sec" id="colour">
        <div className="wrap wide">
          <div className="head">
            <div className="mono eyebrow or">Colour &amp; pipeline</div>
            <h2>
              Colour you can <span className="inkword">trust.</span>
            </h2>
            <p className="lead">
              ACEScg working space, OCIO management and ACEScc / ACEScct viewing. The data and the view stay
              separate, the way a pipeline expects.
            </p>
          </div>
          <div className="vid">
            <Zoomable
              className="box"
              label="Enlarge the colour pipeline demo"
              item={{ type: 'video', src: COLOUR_VIDEO }}
              open={openLightbox}
            >
              <video autoPlay muted loop playsInline preload="metadata" poster={COLOUR_POSTER}>
                <source src={COLOUR_VIDEO} type="video/mp4" />
              </video>
            </Zoomable>
          </div>
        </div>
      </section>

      <svg className="liquid" viewBox="0 0 1440 120" preserveAspectRatio="none" aria-hidden="true">
        <path fill="#130E1D" d="M0 120V60C160 10 300 10 480 50s330 60 520 20 300-50 440-10v60z" />
      </svg>
      <section className="sec band" id="generative" style={{ paddingTop: 60 }}>
        <div className="wrap wide">
          <div className="head">
            <div className="mono eyebrow">Generative editing</div>
            <h2>
              Select it. Change it. <span className="inkword">Keep it yours.</span>
            </h2>
            <p className="lead">
              Remove, replace and extend your image in any colour space, 8/16-bit integer or ACEScg linear 16/32-bit
              float, without needing to convert the image first. Use locally hosted image generation models running
              on your own GPU, a cloud provider through an API, or a combination of both.
            </p>
            <div className="tags">
              <span className="tag">Remove</span>
              <span className="tag">Replace</span>
              <span className="tag">Extend</span>
            </div>
          </div>
          <div className="vid">
            <Zoomable
              className="box"
              label="Enlarge the generative editing demo"
              item={{ type: 'video', src: GEN_VIDEO }}
              open={openLightbox}
            >
              <video autoPlay muted loop playsInline preload="metadata" poster={GEN_POSTER}>
                <source src={GEN_VIDEO} type="video/mp4" />
              </video>
            </Zoomable>
          </div>
        </div>
      </section>

      <section className="sec" id="keying">
        <div className="wrap wide">
          <div className="head">
            <div className="mono eyebrow or">Keying &amp; Scene Splitter</div>
            <h2>
              Key the plate. <span className="inkword">Split the painting.</span>
            </h2>
            <p className="lead">
              Two tools, one pass: pull a clean key from a green or blue screen plate, then break a flat matte
              painting into depth layers, ready to comp.
            </p>
            <div className="tags">
              <span className="tag">Green / Blue Screen</span>
              <span className="tag">Despill</span>
              <span className="tag">Scene Splitter</span>
              <span className="tag">Depth layers</span>
            </div>
          </div>
          <div className="vid">
            <Zoomable
              className="box"
              label="Enlarge the keying and Scene Splitter demo"
              item={{ type: 'video', src: KEY_SPLIT_VIDEO }}
              open={openLightbox}
            >
              <video autoPlay muted loop playsInline preload="metadata" poster={KEY_SPLIT_POSTER}>
                <source src={KEY_SPLIT_VIDEO} type="video/mp4" />
              </video>
            </Zoomable>
          </div>
          <div className="routes two">
            {KEY_SPLIT_TOOLS.map((t) => (
              <div className="route" key={t.k}>
                <small>{t.n}</small>
                <b>{t.k}</b>
                <span>{t.d}</span>
                <em>{t.tag}</em>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="sec" id="local-ai">
        <div className="wrap center">
          <div className="mono eyebrow">Your AI, your rules</div>
          <h2 style={{ maxWidth: 'none' }}>
            Run it <span className="inkword">where you choose.</span>
          </h2>
          <p className="lead">
            Go fully air-gapped, use your studio&apos;s servers, or bring your own cloud key. Mix them per task. Only
            the crop and mask are ever sent, and only where you point them.
          </p>
          <div className="routes">
            {ROUTES.map((r) => (
              <div className="route" key={r.k}>
                <small>{r.k}</small>
                <b>{r.t}</b>
                <span>{r.d}</span>
                <em>{r.tag}</em>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="finale">
        <div className="wrap">
          <img className="ic" src={LOGO_256} alt="" width="120" height="120" />
          <h2>
            Free for personal use.
            <br />
            <span className="inkword">Built for VFX.</span>
          </h2>
          <p className="lead">
            Use Neevii Ink free for your own projects, learning and hobby work. Commercial licensing is coming soon.{' '}
            <Link to="/get-ink">Licence details →</Link>
          </p>
          <div className="cta">
            <Link className="btn btn-p big" to="/get-ink#download">↓ Download for Linux</Link>
            <Link className="btn btn-g big" to="/get-ink#download">↓ Download for Windows</Link>
          </div>
        </div>
      </section>

      <InkFooter />
      {lightbox}
    </div>
  );
}
